import { useCallback, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import httpInstances from '@/api/axiosInstance';
import { DEFAULT_INDEX_GROUP } from '@/constants/constants';
import type { SymbolKey } from '@/entities/symbol';
import { pastExportsOptions } from '@/features/DataHub/api';
import { PAST_EXPORTS_REFETCH_DELAY_MS } from '@/features/DataHub/constants/pastExports';
import { optionById } from '@/features/DataHub/modules/Export/constants/exportOptions';
import type {
  ExportRequestOverrides,
  ExportRequestParams,
  ExportSettingsType,
  ExportStatus
} from '@/features/DataHub/modules/Export/types';
import type { ExportItemType } from '@/features/DataHub/types/pastExports';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';
import downloadFile from '@/utils/downloadFile';

import { buildExportRequestBody } from '../services/ExportRequestService';
import { getFromToValues } from '../utils';

const EXPORT_ENDPOINT = 'export';
const EXCEL_EXPORT_TYPE = 'excel';

type ExportResponse = {
  downloadUrl?: string;
  message?: string;
};

const extractParamsFromUrl = (url: string) => {
  const parsed = new URL(url);
  const pathParts = parsed.pathname.split('/').filter(Boolean);
  const fileName = pathParts[1];
  const fileId = pathParts[2];
  return { fileName, fileId };
};

type UseExportMutationParams = {
  exportName: string | null;
  exportSettings: ExportSettingsType;
  symbols: SymbolKey[];
  userTimezone: string;
  userDateFormat: string;
  createExportName: () => string;
  setExportName: (name: string | null) => void;
  setPreviewOptionId: (id: string | null) => void;
};

const useExportMutation = ({
  exportName,
  exportSettings,
  symbols,
  userTimezone,
  userDateFormat,
  createExportName,
  setExportName,
  setPreviewOptionId
}: UseExportMutationParams) => {
  const [exportStatus, setExportStatus] = useState<ExportStatus>('idle');
  const queryClient = useQueryClient();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarNotifications();

  const { fromValue, toValue } = getFromToValues(exportSettings);

  const postAndHandleResponse = async (
    requestBody: ExportRequestParams,
    isRegeneration: boolean,
    snackbarKey: string
  ) => {
    const response = await httpInstances.axiosGoInstance.post<ExportResponse>(
      EXPORT_ENDPOINT,
      requestBody,
      { validateStatus: status => status === 200 || status === 202 }
    );

    if (response.status === 200 && response.data.downloadUrl) {
      const { fileId, fileName } = extractParamsFromUrl(response.data.downloadUrl);
      void downloadFile(fileId, fileName);
      setExportStatus('idle');
      showSuccessSnackbar(
        isRegeneration ? 'Export completed!' : 'Export completed! View in Past Exports anytime',
        snackbarKey
      );
    } else if (response.status === 202) {
      setExportStatus('queued');
    } else {
      setExportStatus('idle');
    }

    return response;
  };

  const { mutateAsync: exportDataMutation, isPending: loading } = useMutation({
    mutationFn: async (params: ExportRequestOverrides) => {
      const selectedOption = optionById.get(params.type);
      const exportSubType = params.exportSubType ?? selectedOption?.exportSubType ?? null;
      const isExcelExport = params.type === EXCEL_EXPORT_TYPE;
      const isRegeneration = Boolean(params.id);

      const requestBody = buildExportRequestBody({
        id: params.id,
        name: params.name ?? exportName,
        exportType: params.type,
        exportSubType,
        from: params.from ?? fromValue,
        to: params.to ?? toValue,
        timezone: params.timezone ?? userTimezone,
        dateFormat: params.dateFormat ?? userDateFormat,
        includeMetadata: params.includeMetadata ?? exportSettings.includeMetadata,
        symbols: params.symbols ?? symbols,
        group: params.group ?? DEFAULT_INDEX_GROUP
      });

      if (isExcelExport) {
        setExportStatus('loading');
      }

      return postAndHandleResponse(
        requestBody,
        isRegeneration,
        isExcelExport ? 'excel-export' : 'export'
      );
    },
    onMutate: async (params: ExportRequestOverrides) => {
      await queryClient.cancelQueries({ queryKey: pastExportsOptions.lists() });

      const previousExports = queryClient.getQueryData<ExportItemType[]>(
        pastExportsOptions.lists()
      );

      if (params.id) {
        queryClient.setQueryData<ExportItemType[]>(pastExportsOptions.lists(), old =>
          old?.map(item =>
            item.id === params.id
              ? {
                  ...item,
                  exportStatus: 'ongoing',
                  savedAt: Date.now(),
                  lastExecutionTime: Date.now()
                }
              : item
          )
        );
      } else {
        const tempId = `temp-${Date.now()}`;
        const selectedOption = optionById.get(params.type);
        const newExport: ExportItemType = {
          id: tempId,
          name: params.name ?? exportName ?? 'New Export',
          exportType: params.type,
          exportSubType: params.exportSubType ?? selectedOption?.exportSubType ?? null,
          savedAt: Date.now(),
          lastExecutionTime: Date.now(),
          exportStatus: 'ongoing',
          exportStatusDetails: null,
          exportSettings: {
            from: params.from ?? fromValue,
            to: params.to ?? toValue,
            timezone: params.timezone ?? userTimezone,
            dateFormat: params.dateFormat ?? userDateFormat,
            includeMetadata: params.includeMetadata ?? exportSettings.includeMetadata
          },
          symbols: (params.symbols ?? symbols).map(s => ({
            key: {
              code: s.Code,
              periodType: s.PeriodType,
              timeRef: s.TimeRef,
              period: s.Period
            },
            group: params.group ?? DEFAULT_INDEX_GROUP
          }))
        };

        queryClient.setQueryData<ExportItemType[]>(pastExportsOptions.lists(), old =>
          old ? [newExport, ...old] : [newExport]
        );
      }

      return { previousExports };
    },
    onSuccess: () => {
      setExportName(createExportName());
      setPreviewOptionId(null);

      setTimeout(() => {
        void queryClient.refetchQueries({ queryKey: pastExportsOptions.lists() });
      }, PAST_EXPORTS_REFETCH_DELAY_MS);
    },
    onError: (_error, _params, context) => {
      if (context?.previousExports) {
        queryClient.setQueryData(pastExportsOptions.lists(), context.previousExports);
      }
      setExportStatus('idle');
      showErrorSnackbar(
        'Oops! Something went wrong. Please try again later.',
        3000,
        'export-error'
      );
    }
  });

  const resetExportStatus = useCallback(() => {
    setExportStatus('idle');
  }, []);

  return {
    exportDataMutation,
    loading,
    exportStatus,
    setExportStatus,
    resetExportStatus
  };
};

export default useExportMutation;
